import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

interface TicketPaginationProps {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (p: number) => void;
}

export function TicketPagination({ page, totalPages, total, onPageChange }: TicketPaginationProps) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
      <p className="text-sm text-muted-foreground order-2 sm:order-1">
        Page {page} of {totalPages} &middot; {total} total
      </p>
      <div className="flex items-center gap-1 order-1 sm:order-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={page <= 1}
          aria-label="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Previous</span>
        </Button>
        <span className="sm:hidden text-sm text-muted-foreground px-2 font-mono-data">
          {page} / {totalPages}
        </span>
        <div className="hidden sm:flex items-center gap-1">
          <PageNumbers page={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages}
          aria-label="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function PageNumbers({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  const pages = buildPageList(page, totalPages);
  return (
    <>
      {pages.map((p, i) =>
        p === null ? (
          <span key={`ellipsis-${i}`} className="px-2 text-muted-foreground text-sm">
            …
          </span>
        ) : (
          <Button
            key={p}
            variant={p === page ? "default" : "outline"}
            size="sm"
            className="w-9"
            onClick={() => onPageChange(p)}
          >
            {p}
          </Button>
        )
      )}
    </>
  );
}

function buildPageList(current: number, total: number): (number | null)[] {
  if (total <= 4) return Array.from({ length: total }, (_, i) => i + 1);
  const w1 = Math.max(2, Math.min(current - 1, total - 2));
  const w2 = Math.min(w1 + 1, total - 1);
  const pages: (number | null)[] = [1];
  if (w1 > 2) pages.push(null);
  pages.push(w1);
  if (w1 !== w2) pages.push(w2);
  if (w2 < total - 1) pages.push(null);
  pages.push(total);
  return pages;
}
